import { Injectable, inject } from '@angular/core';
import { WINDOW } from '@campus/browser';
import { ENVIRONMENT_API_TOKEN, ENVIRONMENT_LOGIN_TOKEN, ENVIRONMENT_WEBSITE_TOKEN } from '@campus/environment';
import { PersonInterface } from '@campus/public-portal';

/**
 * Query param the api adds to the login url when it bounces an unauthenticated
 * user off a deep link (see `getRedirectUrlForUnauthenticatedUsers` in the
 * task/bundle adapters). It holds the url to send the user to after login.
 */
export const REDIRECT_AFTER_LOGIN_PARAM = 'redirectAfterLogin';

@Injectable({
  providedIn: 'root',
})
export class RedirectService {
  private environment = inject(ENVIRONMENT_LOGIN_TOKEN);
  private environmentApi = inject(ENVIRONMENT_API_TOKEN);
  private environmentWebsite = inject(ENVIRONMENT_WEBSITE_TOKEN);
  private window = inject(WINDOW);

  public toPlatform(user: PersonInterface) {
    if (!user) return;

    const type = user.types?.[0] || 'teacher';
    const platformUrl = this.getPlatformUrl(type);

    let url: string;
    try {
      url = this.getRedirectAfterLoginUrl(platformUrl) || platformUrl;
    } catch (error) {
      // A blocked redirect target: don't fall back to the platform either,
      // the redirect is refused outright. Swallowing the error here keeps the
      // login effect and the router guard that call us alive.
      console.error(error);
      return;
    }

    if (url) this.redirect(url);
  }

  public getPlatformUrl(type: string) {
    return this.environment[`${type}AppUrl`] || this.environment.teacherAppUrl;
  }

  /**
   * The url from the `redirectAfterLogin` query param, or null when it is
   * absent. A relative path is resolved against the platform the user is being
   * sent to. An absolute url is only accepted for one of our own origins —
   * anything else would turn the login page into an open redirect, so it
   * throws instead of being redirected to.
   */
  public getRedirectAfterLoginUrl(platformUrl: string): string {
    const target = new URLSearchParams(this.window.location.search || '').get(REDIRECT_AFTER_LOGIN_PARAM);
    if (!target) return null;

    if (this.isRelativePath(target)) {
      return platformUrl ? platformUrl.replace(/\/$/, '') + target : null;
    }

    if (!this.isOwnOrigin(target)) {
      throw new Error(`Blocked ${REDIRECT_AFTER_LOGIN_PARAM} url outside our own origins: ${target}`);
    }

    return target;
  }

  public redirect(url: string) {
    this.window.location.assign(url);
  }

  private isRelativePath(target: string): boolean {
    // `//evil.com` is protocol-relative, so it is not a path but another origin
    return target.startsWith('/') && !target.startsWith('//');
  }

  private isOwnOrigin(target: string): boolean {
    const origin = this.getOrigin(target);

    return !!origin && this.getOwnOrigins().includes(origin);
  }

  private getOwnOrigins(): string[] {
    return [
      this.environmentApi?.APIBase,
      this.environment?.teacherAppUrl,
      this.environment?.studentAppUrl,
      this.environment?.schooladminAppUrl,
      this.environmentWebsite?.url,
    ]
      .map((url) => this.getOrigin(url))
      .filter((origin) => !!origin);
  }

  private getOrigin(url: string): string {
    if (!url) return null;

    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }
}
